import { View, StyleSheet, Text, Pressable, ScrollView, Linking, Keyboard } from "react-native";
import { useState, useEffect } from "react";
import { faCube, faTriangleExclamation, faSparkles, faGear } from "@fortawesome/pro-regular-svg-icons";
import { faBuilding } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon, tabIconSize, os, logError } from "../platform";
import AppStore from "../storage/appStore";
import AddressLookup from "../components/HomeComponents/AddressLookup";
import PropertyInfo from "../components/HomeComponents/PropertyInfo";
import Alerts from "../components/HomeComponents/Alerts";
import { getAppData } from "../utils/api/appDataApi";
import { COLORS } from "../theme/colors";
import { STRINGS } from "../constants/strings";
import FabButton from "../sub/components/FabButton";

const Home = ({ navigation }) => {
  const [suggestedAddresses, setSuggestedAddresses] = useState([]);
  const propertyInfo = AppStore((state) => state.propertyInfo);
  const alerts = AppStore((state) => state.alerts);
  const setLoading = AppStore((state) => state.setLoading);
  const setErrorMessage = AppStore((state) => state.setErrorMessage);
  const setAlerts = AppStore((state) => state.setAlerts);
  const setLinks = AppStore((state) => state.setLinks);
  const setPayLinks = AppStore((state) => state.setPayLinks);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getAppData();

        setAlerts(data.alerts || []);
        setLinks(data.links || []);
        setPayLinks(data.payLinks || []);
      } catch (error) {
        logError("app_data_fetch_failed", {
          message: error.message,
          stack: error.stack,
        });
        setErrorMessage(STRINGS.failedToLoadData);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [setLoading, setErrorMessage, setAlerts, setLinks, setPayLinks]);

  /**
   * Hides the keyboard (on non-web platforms) and clears the suggested addresses dropdown.
   *
   * @function
   * @returns {void}
   */
  const hideKeyboardAndDropdown = () => {
    if (os !== "web") {
      Keyboard.dismiss();
    }
    setSuggestedAddresses([]);
  };

  return (
    <View style={styles.rootTouchable} accessible={false} testID="home-container">
      <View style={styles.fabContainerRight}>
        <FabButton
          testID="settings-button"
          customStyle={styles.fabButton}
          icon={faGear}
          iconColor={COLORS.text}
          onPress={() => navigation.navigate("Settings")}
          accessibilityLabel="Settings"
        />
      </View>
      <ScrollView
        contentContainerStyle={styles.container}
        keyboardShouldPersistTaps="handled"
        onScrollBeginDrag={hideKeyboardAndDropdown}
      >
        {alerts?.length > 0 && <Alerts alerts={alerts} />}
        {propertyInfo?.gpin ? (
          <PropertyInfo />
        ) : (
          <View style={styles.addressLookupContainer}>
            <AddressLookup
              suggestedAddresses={suggestedAddresses}
              setSuggestedAddresses={setSuggestedAddresses}
              hideKeyboardAndDropdown={hideKeyboardAndDropdown}
            />
          </View>
        )}

        <View style={styles.buttonsContainer}>
          <View style={styles.buttonsRowContainer}>
            <Pressable
              style={styles.buttonItem}
              onPress={() => {
                hideKeyboardAndDropdown();
                if (os === "web") {
                  Linking.openURL(STRINGS.bulkPickupLink);
                } else {
                  navigation.navigate("WebViewScreen", { url: STRINGS.bulkPickupLink, title: STRINGS.bulkPickup });
                }
              }}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={STRINGS.bulkPickup}
            >
              <FontAwesomeIcon icon={faCube} size={tabIconSize} color={COLORS.textDark} />
              <Text style={styles.buttonItemText} maxFontSizeMultiplier={1.2}>
                {STRINGS.bulkPickup}
              </Text>
            </Pressable>
            <Pressable
              style={styles.buttonItem}
              onPress={() => {
                hideKeyboardAndDropdown();
                if (os === "web") {
                  Linking.openURL(STRINGS.streetSweepingLink);
                } else {
                  navigation.navigate("WebViewScreen", { url: STRINGS.streetSweepingLink, title: STRINGS.streetSweeping });
                }
              }}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={STRINGS.streetSweeping}
            >
              <FontAwesomeIcon icon={faSparkles} size={tabIconSize} color={COLORS.textDark} />
              <Text style={styles.buttonItemText} maxFontSizeMultiplier={1.2}>
                {STRINGS.streetSweeping}
              </Text>
            </Pressable>
          </View>
          <View style={styles.buttonsRowContainer}>
            <Pressable
              style={styles.buttonItem}
              onPress={() => {
                hideKeyboardAndDropdown();
                if (os === "web") {
                  Linking.openURL(STRINGS.reportIssueLink);
                } else {
                  navigation.navigate("WebViewScreen", { url: STRINGS.reportIssueLink, title: STRINGS.reportIssue });
                }
              }}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={STRINGS.reportIssue}
            >
              <FontAwesomeIcon icon={faTriangleExclamation} size={tabIconSize} color={COLORS.textDark} />
              <Text style={styles.buttonItemText} maxFontSizeMultiplier={1.2}>
                {STRINGS.reportIssue}
              </Text>
            </Pressable>
            <Pressable
              style={styles.buttonItem}
              onPress={() => {
                hideKeyboardAndDropdown();
                if (os === "web") {
                  Linking.openURL(STRINGS.centersLink);
                } else {
                  navigation.navigate("WebViewScreen", { url: STRINGS.centersLink, title: STRINGS.centers });
                }
              }}
              accessible={true}
              accessibilityRole="button"
              accessibilityLabel={STRINGS.centers}
            >
              <FontAwesomeIcon icon={faBuilding} size={tabIconSize} color={COLORS.textDark} />
              <Text style={styles.buttonItemText} maxFontSizeMultiplier={1.2}>
                {STRINGS.centers}
              </Text>
            </Pressable>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  rootTouchable: {
    height: "100%",
    flex: 1,
  },
  fabContainerRight: {
    position: "absolute",
    right: 10,
    bottom: 0,
    zIndex: 1000,
  },
  fabContainerRightLandscape: {
    right: 10,
    bottom: 10,
  },
  fabButton: {
    marginBottom: 10,
    width: os === "web" ? 35 : 40,
    height: os === "web" ? 35 : 40,
    borderRadius: 9999,
    backgroundColor: "#fff",
  },
  container: {
    flexGrow: 1,
    width: "100%",
    maxWidth: 750,
    alignSelf: "center",
    paddingHorizontal: 10,
    paddingTop: 10,
    paddingBottom: 50,
    backgroundColor: COLORS.background,
  },
  addressLookupContainer: {
    zIndex: 10,
  },
  buttonsContainer: {
    flexDirection: "column",
    gap: 15,
    marginTop: 15,
    zIndex: 1,
  },
  buttonsRowContainer: {
    flexDirection: "row",
    gap: 15,
  },
  buttonItem: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: 15,
    gap: 10,
    paddingVertical: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  buttonItemText: {
    fontSize: 14,
    fontWeight: "bold",
    textAlign: "center",
  },
});

export default Home;
